import type { FC } from 'react';
import type { BookBaseColor, SalesValues } from 'shared/types/index';

import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { COLOR_RED_3 } from 'shared/helpers/colors';
import { sanitizeRichText } from 'shared/helpers/utils';

import useSales from 'shared/hooks/useSales';
import useWebsiteEditor from 'shared/hooks/useWebsiteEditor';
import { filterBackgroundColor } from 'shared/helpers/website';

import ButtonActionModal from 'shared/components/Common/ButtonActionModal';
import ButtonSettingIcon from './ButtonSettingIcon';

const ButtonInner = styled.div`
  position: relative;
  width: fit-content;

  @media screen and (max-width: 575px) {
    width: 100%;
    padding: 0 2.5%;
  }
`;

const StyledButton = styled.a<{ editable: boolean }>`
  all: unset;
  min-width: 200px;
  min-height: 10px;
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  font-weight: 500;
  padding: 24px 40px;
  border-radius: 40px;
  text-align: center;
  transition:
    transform 0.2s ease 0s,
    box-shadow 0.2s ease 0s;
  cursor: pointer;
  display: block;
  max-width: 600px;

  ${(props) =>
    props.editable
      ? 'cursor: text; word-break: break-word;'
      : `
  &:hover {
    transform: translateY(-3px);
    box-shadow: rgba(0, 0, 0, 0.2) 0px 1rem 2rem;
    transition:
      transform 0.2s ease 0s,
      box-shadow 0.2s ease 0s;
  }
  `}

  @media (max-width: 991px) {
    padding: 17px 28px;
  }

  @media (max-width: 768px) {
    &:hover {
      transform: translateY(0px);
      box-shadow: rgba(0, 0, 0, 0) 0px 0rem 0rem;
    }
  }

  @media (max-width: 575px) {
    width: -webkit-fill-available;
    padding: 25px 20px;
  }

  @media (max-width: 500px) {
    padding: 19px 32px;
  }

  @media (max-width: 380px) {
    padding: 16px 27px;
  }

  @media (max-width: 340px) {
    padding: 16px 25px;
  }

  & p {
    font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
    display: inline;
  }
`;

export interface DownloadProps {
  button: SalesValues;
  textColor: BookBaseColor;
  benefitIndex?: number;
  outerOnClick?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  meta: Record<string, any>;
  isBotPosition?: boolean;
  className?: string;
}

export const Download: FC<DownloadProps> = (props) => {
  const { button, meta, textColor, outerOnClick, benefitIndex, isBotPosition, className } = props;

  const [isButtonActionModalOpen, setIsButtonActionModalOpen] = useState(false);
  const [showLinkIcon, setShowLinkIcon] = useState(false);

  const { downloadUrl, communityUrl, type, eventDetails } = useSales();
  const { updateActiveElement, resetActiveElement, showLink, editable } = useWebsiteEditor();

  const href = useMemo(() => {
    if (meta['download']) return downloadUrl;
    if (meta['community']) return communityUrl;
    if (meta['url']) return meta['url'];
    if (benefitIndex) return meta[`url_${benefitIndex}`];
    if (meta['event']) return eventDetails?.calendarUrl;
    if (type === 'delivery') return downloadUrl;

    return '#';
  }, [communityUrl, downloadUrl, eventDetails?.calendarUrl, meta, type, benefitIndex]);

  return (
    <ButtonInner
      onMouseEnter={() => setShowLinkIcon(true)}
      onMouseLeave={() => setShowLinkIcon(isButtonActionModalOpen)}>
      <StyledButton
        style={{
          backgroundColor: button?.color || COLOR_RED_3,
          color: filterBackgroundColor(button?.color || COLOR_RED_3, textColor)
        }}
        className={className}
        editable={process.env['APP_TYPE'] === 'dashboard'}
        id={button.id}
        aria-label={button.val ? `Download: ${button.val}` : 'Download button'}
        dangerouslySetInnerHTML={{
          __html: sanitizeRichText(
            button?.val?.trim() || '<p><br></p>',
            process.env['APP_TYPE'] === 'dashboard',
            false,
            benefitIndex ? 'Get Started Now' : '<p><br></p>'
          )
        }}
        data-accessor-parent={meta['accessorKey']}
        data-accessorkey="button"
        onClick={(e) => {
          updateActiveElement(e);
          outerOnClick?.();
        }}
        onBlur={resetActiveElement}
        {...(process.env['APP_TYPE'] === 'dashboard' || showLink
          ? { as: 'p' }
          : { href, target: '_blank' })}
        {...(meta['community'] ? { 'data-community': true } : {})}
        {...(meta['download'] ? { 'data-anchor-disabled': true } : {})}
        {...(benefitIndex ? { 'data-benefits': true, 'data-pindex': benefitIndex } : {})}
      />

      {process.env['APP_TYPE'] === 'dashboard' && editable && showLinkIcon ? (
        <ButtonSettingIcon
          setIsButtonActionModalOpen={setIsButtonActionModalOpen}
          setShowLinkIcon={setShowLinkIcon}
        />
      ) : null}

      {isButtonActionModalOpen && process.env['APP_TYPE'] === 'dashboard' ? (
        <ButtonActionModal
          onClose={() => {
            setIsButtonActionModalOpen(false);
            setShowLinkIcon(false);
          }}
          isBotPosition={isBotPosition}
          meta={meta ?? {}}
        />
      ) : null}
    </ButtonInner>
  );
};
